import { useEffect, useState } from "react";
import { api, money } from "@/hub/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, XCircle, Plus } from "lucide-react";
import { toast } from "sonner";

const Section = ({ n, label, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-2">
      <span className="h-5 w-5 rounded-sm bg-forest text-white text-[10px] font-mono flex items-center justify-center">{n}</span>
      <Label className="text-xs uppercase tracking-[0.15em] text-stone-500">{label}</Label>
    </div>
    {children}
  </div>
);

export function BookingSheet({ defaultDate, defaultGroupId, groups = [], packages = [], activities = [], onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("Leisure");
  const [groupId, setGroupId] = useState(defaultGroupId || "");
  const [from, setFrom] = useState(defaultDate || "");
  const [to, setTo] = useState(defaultDate || "");
  const [avail, setAvail] = useState([]);
  const [zoneId, setZoneId] = useState("");
  const [accReq, setAccReq] = useState(false);
  const [accType, setAccType] = useState("");
  const [packageId, setPackageId] = useState("");
  const [meals, setMeals] = useState([]);
  const [acts, setActs] = useState([]);
  const [advance, setAdvance] = useState(false);
  const [advanceAmt, setAdvanceAmt] = useState("");
  const [pax, setPax] = useState("");
  const [checkinTime, setCheckinTime] = useState("");
  const [checkoutTime, setCheckoutTime] = useState("");
  const [mealOptions, setMealOptions] = useState([]);
  const [allGroups, setAllGroups] = useState(groups);
  const [newClient, setNewClient] = useState("");
  const [newClientPhone, setNewClientPhone] = useState("");
  const [newClientEmail, setNewClientEmail] = useState("");
  const [addingClient, setAddingClient] = useState(false);

  useEffect(() => { api.meals().then(setMealOptions); }, []);
  useEffect(() => { setAllGroups(groups); }, [groups]);

  useEffect(() => {
    if (open) {
      setType("Leisure"); setGroupId(defaultGroupId || ""); setFrom(defaultDate || ""); setTo(defaultDate || "");
      setZoneId(""); setAccReq(false); setAccType(""); setPackageId(""); setMeals([]); setActs([]);
      setAdvance(false); setAdvanceAmt(""); setPax("");
      setNewClient(""); setNewClientPhone(""); setNewClientEmail(""); setAddingClient(false);
      setCheckinTime(""); setCheckoutTime("");
    }
  }, [open, defaultDate, defaultGroupId]);

  useEffect(() => {
    if (from && to && to >= from) api.availabilityRange(from, to).then(setAvail);
    else setAvail([]);
  }, [from, to]);

  const outdoor = avail.filter((a) => a.zone.type === "outdoor");
  const accom = avail.filter((a) => a.zone.type === "accommodation");
  const selectedPackage = packages.find((p) => p.id === packageId);
  const isCustom = packageId === "__custom__";
  const includedIds = new Set((selectedPackage?.items || []).map((i) => i.activity_id));
  const pkgList = packages.filter((p) => (type === "Training" ? p.type === "Training" : p.type !== "Training"));

  const toggleMeal = (m) => setMeals((ms) => (ms.includes(m) ? ms.filter((x) => x !== m) : [...ms, m]));
  const toggleAct = (a) =>
    setActs((list) => (list.find((x) => x.activity_id === a.id)
      ? list.filter((x) => x.activity_id !== a.id)
      : [...list, { activity_id: a.id, name: a.name, price: a.cost, qty: 1 }]));
  const setActQty = (id, q) => setActs((list) => list.map((x) => (x.activity_id === id ? { ...x, qty: Number(q) || 1 } : x)));

  const addTotal = acts.reduce((s, a) => s + a.price * a.qty, 0);
  const total = (selectedPackage?.base_price || 0) + addTotal;

  const createClient = async () => {
    if (!newClient.trim()) return toast.error("Enter a client name");
    try {
      const g = await api.createGroup({
        name: newClient.trim(), phone: newClientPhone.trim(), email: newClientEmail.trim(),
        rm_name: localStorage.getItem("lush_rm_name") || "", add_ons: [],
      });
      setAllGroups((x) => [g, ...x]);
      setGroupId(g.id);
      setNewClient(""); setNewClientPhone(""); setNewClientEmail(""); setAddingClient(false);
      toast.success("Client group created");
      onDone && onDone();
    } catch {
      toast.error("Could not create client");
    }
  };

  const submit = async () => {
    if (!defaultGroupId && !newClient.trim()) return toast.error("Enter the client name");
    if (!from || !to) return toast.error("Select the reservation date range");
    if (to < from) return toast.error("End date must be on/after start date");
    if (!zoneId) return toast.error("Select an available zone");
    if (accReq && !accType) return toast.error("Select an accommodation type");
    try {
      let gid = groupId;
      if (!defaultGroupId) {
        const g = await api.createGroup({
          name: newClient.trim(), email: newClientEmail.trim(), phone: newClientPhone.trim(),
          rm_name: localStorage.getItem("lush_rm_name") || "", add_ons: [],
        });
        gid = g.id;
      }
      await api.createBooking({
        group_id: gid, type, start_date: from, end_date: to, zone_id: zoneId,
        accommodation_required: accReq, accommodation_type_id: accReq ? accType : null,
        package_id: (packageId && packageId !== "__custom__") ? packageId : null,
        custom_package: isCustom,
        meals, additional_activities: acts,
        advance_paid: advance, advance_amount: Number(advanceAmt) || 0, pax: Number(pax) || 0,
        checkin_time: checkinTime, checkout_time: checkoutTime,
      });
      toast.success("Booking created");
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create booking");
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent className="overflow-y-auto lush-scroll w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="font-heading">New Booking</SheetTitle>
          <SheetDescription>Build a complete reservation and attach it to a client group.</SheetDescription>
        </SheetHeader>

        <div className="space-y-5 mt-5">
          <Section n="1" label="Booking Type">
            <div className="grid grid-cols-2 gap-2">
              {["Leisure", "Training"].map((t) => (
                <button key={t} data-testid={`booking-type-${t.toLowerCase()}`}
                  onClick={() => { setType(t); setPackageId(""); }}
                  className={`py-2.5 rounded-sm text-sm font-medium border transition-colors ${type === t ? "bg-forest text-white border-forest" : "border-border text-stone-600 hover:bg-stone-50"}`}>
                  {t}
                </button>
              ))}
            </div>
          </Section>

          <Section n="2" label="Client">
            {defaultGroupId ? (
              <div className="text-sm font-medium px-3 py-2 border border-border rounded-sm bg-stone-50" data-testid="booking-client-locked">{allGroups.find((g) => g.id === groupId)?.name || "Selected client"}</div>
            ) : (
              <div className="space-y-2">
                <Input value={newClient} onChange={(e) => setNewClient(e.target.value)} placeholder="Client name" className="rounded-sm" data-testid="booking-new-client-input" />
                <Input value={newClientEmail} onChange={(e) => setNewClientEmail(e.target.value)} placeholder="Email" className="rounded-sm" data-testid="booking-new-client-email" />
                <Input value={newClientPhone} onChange={(e) => setNewClientPhone(e.target.value)} placeholder="Contact number" className="rounded-sm" data-testid="booking-new-client-phone" />
              </div>
            )}
          </Section>

          <Section n="3" label="Reservation Dates (from – to)">
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm" data-testid="booking-from" />
              <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="rounded-sm" data-testid="booking-to" />
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div><Label className="text-[10px] uppercase tracking-wider text-stone-400">Check-in</Label><Input type="time" value={checkinTime} onChange={(e) => setCheckinTime(e.target.value)} className="rounded-sm" data-testid="booking-checkin" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-stone-400">Check-out</Label><Input type="time" value={checkoutTime} onChange={(e) => setCheckoutTime(e.target.value)} className="rounded-sm" data-testid="booking-checkout" /></div>
            </div>
          </Section>

          <Section n="4" label="Available Zones">
            {avail.length === 0 ? (
              <p className="text-xs text-stone-400">Select dates to see available zones.</p>
            ) : (
              <Select value={zoneId} onValueChange={setZoneId}>
                <SelectTrigger className="rounded-sm" data-testid="booking-zone-select"><SelectValue placeholder="Select zone" /></SelectTrigger>
                <SelectContent>
                  {outdoor.map((a) => (
                    <SelectItem key={a.zone.id} value={a.zone.id} disabled={!a.available}>
                      <span className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: a.zone.color }} />
                        {a.zone.name} (cap {a.zone.capacity})
                        {a.available
                          ? <span className="text-emerald-600 text-xs ml-1">· free</span>
                          : <span className="text-red-500 text-xs ml-1">· {a.reserved_by}</span>}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Section>

          <Section n="5" label="Accommodation Required">
            <div className="flex items-center gap-3">
              <Switch checked={accReq} onCheckedChange={setAccReq} data-testid="booking-acc-switch" />
              <span className="text-sm text-stone-600">{accReq ? "Yes" : "No"}</span>
            </div>
            {accReq && (
              <div className="mt-2">
                <Select value={accType} onValueChange={setAccType}>
                  <SelectTrigger className="rounded-sm" data-testid="booking-acc-select"><SelectValue placeholder="Accommodation type" /></SelectTrigger>
                  <SelectContent>
                    {accom.map((a) => (
                      <SelectItem key={a.zone.id} value={a.zone.id} disabled={!a.available}>
                        {a.zone.name} (cap {a.zone.capacity}) {a.available ? "" : `· ${a.reserved_by}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </Section>

          <Section n="6" label={`Basic Package Plan (Day ${type})`}>
            <Select value={packageId} onValueChange={setPackageId}>
              <SelectTrigger className="rounded-sm" data-testid="booking-package-select"><SelectValue placeholder="Select package" /></SelectTrigger>
              <SelectContent>
                {pkgList.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {money(p.base_price)}</SelectItem>)}
                <SelectItem value="__custom__" data-testid="booking-package-custom">Customized Package (build your own)</SelectItem>
              </SelectContent>
            </Select>
            {isCustom && <p className="text-[11px] text-stone-400 mt-1">No fixed rate — total is the sum of the activities you add below.</p>}
          </Section>

          <Section n="7" label="Meal Arrangements">
            <div className="grid grid-cols-2 gap-1.5">
              {mealOptions.map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer py-0.5" data-testid={`meal-${m.name.replace(/\s/g, "-")}`}>
                  <Checkbox checked={meals.includes(m.name)} onCheckedChange={() => toggleMeal(m.name)} className="rounded-sm" />
                  <span className="text-stone-600">{m.name}</span>
                </label>
              ))}
            </div>
          </Section>

          <Section n="8" label="Activities Included in Plan">
            {selectedPackage ? (
              <div className="flex flex-wrap gap-1.5">
                {selectedPackage.inclusions.map((inc, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded-sm">
                    <Check className="h-3 w-3 text-forest" /> {inc}
                  </span>
                ))}
              </div>
            ) : isCustom ? <p className="text-xs text-stone-400">Customized package — build the booking from the options below.</p>
              : <p className="text-xs text-stone-400">Select a package to view included activities.</p>}
          </Section>

          <Section n="9" label="Additional Activities Needed">
            <div className="border border-border rounded-sm divide-y divide-border max-h-56 overflow-auto lush-scroll">
              {activities.map((a) => {
                const sel = acts.find((x) => x.activity_id === a.id);
                const included = includedIds.has(a.id);
                return (
                  <div key={a.id} className={`flex items-center gap-2 px-3 py-2 ${included ? "opacity-60 bg-stone-50" : ""}`} data-testid={`addact-${a.id}`}>
                    <Checkbox checked={included ? true : !!sel} disabled={included} onCheckedChange={() => !included && toggleAct(a)} className="rounded-sm" />
                    <span className="text-sm flex-1">{a.name}</span>
                    {included ? (
                      <span className="text-[10px] uppercase tracking-wider text-forest bg-forest/10 px-1.5 py-0.5 rounded-sm" data-testid={`addact-included-${a.id}`}>In package</span>
                    ) : (
                      <>
                        <span className="font-mono text-xs text-stone-400">{money(a.cost)}</span>
                        {sel && (
                          <Input type="number" min="1" value={sel.qty} onChange={(e) => setActQty(a.id, e.target.value)}
                            className="rounded-sm w-14 h-7 text-xs" data-testid={`addact-qty-${a.id}`} />
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>

          <Section n="10" label="Advance Paid">
            <div className="flex items-center gap-3">
              <Switch checked={advance} onCheckedChange={setAdvance} data-testid="booking-advance-switch" />
              <span className="text-sm text-stone-600">{advance ? "Yes" : "No"}</span>
              {advance && (
                <Input type="number" placeholder="Amount LKR" value={advanceAmt} onChange={(e) => setAdvanceAmt(e.target.value)}
                  className="rounded-sm h-8 w-32 font-mono" data-testid="booking-advance-amount" />
              )}
            </div>
          </Section>

          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-stone-500">Pax</Label>
            <Input type="number" value={pax} onChange={(e) => setPax(e.target.value)} placeholder="Group size" className="rounded-sm mt-1" data-testid="booking-pax" />
          </div>
        </div>

        <SheetFooter className="mt-6">
          <Button onClick={submit} className="rounded-sm bg-forest hover:bg-forest-hover w-full" data-testid="submit-booking">
            Add Booking · {money(total)}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
